/* eslint-disable @typescript-eslint/no-explicit-any */

import { Message } from 'protobufjs';
import { batch } from 'react-redux';
import { filter, forEach, includes, isEmpty, isError, isObject, map, omitBy, replace, split, take, toInteger, toString } from 'es-toolkit/compat';
import { tocktix as libProto } from '@tock/protos/dist/types/lib';
import Fingerprint2 from 'fingerprintjs2';

import fetch from 'cross-fetch';
import storeBackend from 'store';

import { DEFAULT_AJAX_ERROR_MESSAGE, logger } from '@tock/logging';
import { StoreType } from './connect';
import {
  asMessageSet,
  getExtension,
  parseProto,
  protoAsMessageSet,
  protoLib,
  safeToNumber,
  toArrayBuffer,
  toJSON,
  toJSONArray,
} from '@tock/coretypes';
import { validateShippingStateOrGetDefault } from './shippingStateUtils';

const {
  SingleResponseProto,
  ListResponseProto,
  BatchRequestListProto,
  BatchRequestProto,
  RequestScope: RequestScopeProto,
} = protoLib.lookup('tocktix.lib') as any;

export interface RequestOptionsTemplate {
  app: Partial<StoreType['app']>;
  experimentVariantHeader?: string;
  cacheOptions?: CacheOptions;
  context?: any;
  customHeaders?: {
    [key: string]: string;
  };
  errorMapper?: ErrorMapper;
  onSuccess?(response: any): void;
  path: string;
  requestBody?: Uint8Array;
  requestObj?: any;
  scope?: ApiScope;
  shippingState?: string;
  metroAreaId?: number;
  successMessage?: string;
  syndicate?: string;
  type: string;
  discardOldRequests?: boolean;
  requestScope?: RequestScope;
  ignoreDispatch?: boolean;
  signal?: AbortSignal;
  ignoreNetworkErrors?: boolean;
  responseProtoType?: any;
  timeoutMs?: number;
}

interface BatchRequestOptions {
  app: Partial<StoreType['app']>;
  type: string;
  requestScope?: RequestScope;
}

/**
 * Introduced as a solution to https://tock.atlassian.net/browse/TK-10229.
 * There is some downstream code that will check for an error code on this object in the dashboard passcode logic.
 * It feels a bit hacky because this isn't really the type that error handling code is expecting,
 * but has the advantage of a low risk of breaking other flows.
 */
class BatchRequestError extends Error {
  error: { errorCode: number };

  constructor(message: string, error: { errorCode: number }) {
    super(message);
    this.error = error;
  }
}

export enum NonProtoResponseType {
  File,
  RawResponse,
}

interface RequestOptionsObject extends RequestOptionsTemplate {
  method: string;
  responseProtoType:
    | libProto.lib.SingleResponseProto
    | libProto.lib.ListResponseProto
    | NonProtoResponseType.File
    | NonProtoResponseType.RawResponse;
}

const protoHeaders = {
  'Content-Type': 'application/octet-stream',
  'X-Tock-Stream-Format': 'proto2',
  Accept: 'application/octet-stream',
};

const protoResponseHeaders = {
  'X-Tock-Stream-Format': 'proto2',
  Accept: 'application/octet-stream',
};

if (BROWSER) {
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (event.reason) {
      if (event.reason.error && event.reason.error.errorCode) {
        logger.warn('Unhandled rejection in network request: ', toJSON(event.reason.error));
      } else {
        logger.warn(
          'Unhandled rejection (promise: ',
          event.promise,
          ', reason: ',
          event.reason,
          ').'
        );
      }
    } else {
      logger.warn('Unhandled rejection ', event.promise);
    }
  });
}

const readStream = BROWSER
  ? (response: any) => {
      // For all modern browsers.
      if (response.arrayBuffer) {
        return response.arrayBuffer();
      }

      // Another funky way to get array buffer if there is no blob support.
      const body = response._bodyInit;
      if (typeof body === 'string') {
        const buffer: any[] = [];
        for (let i = 0; i < body.length; ++i) {
          // tslint:disable-next-line no-bitwise
          buffer.push(body.charCodeAt(i) & 255); // eslint-disable-line no-bitwise
        }
        // tslint:disable:strict-type-predicates
        return Promise.resolve(typeof Uint8Array !== 'undefined' ? new Uint8Array(buffer) : buffer);
        // tslint:enable:strict-type-predicates
      }
      throw new Error('This is such an old browser, you should try upgrading.');
    }
  : (response: any) => {
      return Promise.resolve(response.buffer());
    };

let fingerprint!: string;
try {
  if (BROWSER && storeBackend.enabled) {
    fingerprint = storeBackend.get('fingerprint');
  }
} catch (e) {
  //eslint-disable-next-line no-console
  console.error(e);
}

const fingerprintBlacklist: { [key: string]: boolean } = {
  userAgent: true,
  screenResolution: true,
  availableScreenResolution: true,
  adBlock: true,
  deviceMemory: true,
  hardwareConcurrency: true,
  plugins: true,
  fonts: true,
  audio: true,
  webgl: true,
  canvas: true,
  timezone: true,
  openDatabase: true,
  enumerateDevices: true,
  fontsFlash: true,
  doNotTrack: true,
};

if (BROWSER) {
  requestIdleCallback(() => {
    Fingerprint2.get({ excludes: fingerprintBlacklist }, (components) => {
      const values = map(
        filter(components, (component) => !fingerprintBlacklist[component.key]),
        (component) => component.value
      );
      // Fingerprint should be a stable machine id, not meant to identify a user but a device for fraud clustering
      fingerprint = toString(Fingerprint2.x64hash128(JSON.stringify(values), 31));
      storeBackend.set('fingerprint', fingerprint);
    });
  });
}

/**
 * Get business header on the basis of the app state.
 */
export function getBusinessHeader(
  app: any,
  scope: ApiScope = {},
  syndicate = ''
): { [key: string]: string } {
  const header: { [key: string]: string } = {};
  let tockScope: ApiScope = {};

  const { activeAuth } = app;

  if (activeAuth) {
    tockScope = {
      partnerId: activeAuth.partnerId ? toString(activeAuth.partnerId) : undefined,
      businessId: activeAuth.businessId ? toString(activeAuth.businessId) : undefined,
      businessGroupId: activeAuth.businessGroupId
        ? toString(activeAuth.businessGroupId)
        : undefined,
      site: activeAuth.site,
      chaseProductCode: activeAuth.chaseProductCode,
      ...scope,
    };
  }

  if (BROWSER && (window as any).setLastTockScope) {
    (window as any).setLastTockScope(tockScope);
  }
  if (!isEmpty(tockScope)) {
    header['X-Tock-Scope'] = JSON.stringify(tockScope);
  }

  if (app.jwtToken) {
    header['X-Tock-Authorization'] = app.jwtToken;
  }

  if (app.sessionToken) {
    header['X-Tock-Session'] = app.sessionToken;
  }

  if (app.csrfToken?.token) {
    header['X-Tock-Csrf-Token'] = app.csrfToken.token;
  }

  if (syndicate) {
    header['X-Tock-Syndicate'] = syndicate;
  }

  if (BROWSER) {
    const buildNumber = (window as any).__BUILD_NUMBER__;
    header['X-Tock-Path'] = window.location.hash || window.location.pathname;
    header['X-Tock-Build-Number'] = buildNumber ? buildNumber : undefined;
  }

  if (fingerprint) {
    header['X-Tock-Fingerprint'] = fingerprint;
  }

  return omitBy(header, (v) => v === undefined);
}

export const getShippingStateHeader = (shippingState?: string): { [key: string]: string } => {
  const header: { [key: string]: string } = {};

  if (shippingState) {
    const validatedShippingState = validateShippingStateOrGetDefault(shippingState);
    header['X-Tock-Shipping-State'] = validatedShippingState;
  }

  return header;
};

export const getMetroAreaHeader = (metroAreaId?: number): { [key: string]: string } => {
  const header: { [key: string]: string } = {};

  if (metroAreaId) {
    header['X-Tock-Metro-Area-Id'] = String(metroAreaId);
  }

  return header;
};

const getExperimentsHeader = (experimentsHeader?: string) => {
  const header: Dictionary<String> = {};

  if (experimentsHeader) {
    header['X-Tock-ExperimentVariantList'] = experimentsHeader;
  }

  return header;
};

function getMappedErrorMessage(
  dispatch: Dispatch,
  errorMapper: ErrorMapper,
  errorCode: number,
  originalErrorMessage?: string
) {
  if (typeof errorMapper === 'string') {
    return errorMapper;
  }
  if (typeof errorMapper === 'function') {
    return (errorMapper as any)(dispatch);
  }

  if (isObject(errorMapper) && (errorMapper[errorCode] !== undefined)) {
    const mapping = errorMapper[errorCode];
    if (typeof mapping === 'string') {
      return mapping;
    } else if (typeof mapping === 'function') {
      return (mapping as any)(dispatch);
    }
  }

  if (isObject(errorMapper) && (errorMapper.default !== undefined)) {
    const mapping = errorMapper.default;
    if (typeof mapping === 'string') {
      return mapping;
    } else if (typeof mapping === 'function') {
      return (mapping as any)(dispatch);
    }
  }

  if (isObject(errorMapper) && errorMapper?.defaultToOriginal) {
    return originalErrorMessage;
  }

  throw new Error(
    'onError must one of String, Function or an Object mapping error codes to String or Function'
  );
}

function getResponse(responseProtoType: any, responseObject: any) {
  switch (responseProtoType) {
    case NonProtoResponseType.RawResponse:
      return responseObject;
    case NonProtoResponseType.File:
      return responseObject.response;
    default:
      return unwrap(responseObject.response, responseProtoType);
  }
}

function getResponseProto(responseProtoType: any, httpResponse: any, buffer: any) {
  let responseProto: any = null;
  try {
    if (httpResponse.status === 204) {
      return {};
    }
    switch (responseProtoType) {
      case NonProtoResponseType.RawResponse: {
        return httpResponse;
      }
      case NonProtoResponseType.File: {
        if (httpResponse.headers.get('content-type') === 'application/json') {
          // Try to decode an error, which will be JSON in a file download
          const json = String.fromCharCode.apply(null, Array.from(new Uint8Array(buffer)));
          responseProto = JSON.parse(json) || {};
          if (!responseProto.error) {
            throw new Error('No error message');
          }
        } else {
          // Didn't decode error proto, so must correctly be a file blob
          responseProto = { response: buffer };
        }
        return responseProto;
      }
      default: {
        return parseProto(buffer, responseProtoType as any);
      }
    }
  } catch (e) {
    console.trace(e); // eslint-disable-line no-console
    throw httpResponse;
  }
}

function unwrap<T>(genericMessage?: any | any[], responseProtoType?: any): T {
  if ((genericMessage === null) || (typeof genericMessage === 'boolean')) {
    // isBoolean for requests that return true/false not in a proto wrapper
    return genericMessage as any;
  }

  if (
    Array.isArray(genericMessage) ||
    (responseProtoType && responseProtoType.name === ListResponseProto.name)
  ) {
    return (genericMessage || []).map(unwrap);
  }

  if (Object.prototype.hasOwnProperty.call(genericMessage, 'boolValue')) {
    return genericMessage.boolValue;
  }
  if (Object.prototype.hasOwnProperty.call(genericMessage, 'intValue')) {
    return genericMessage.intValue;
  }
  if (Object.prototype.hasOwnProperty.call(genericMessage, 'decimalValue')) {
    return genericMessage.decimalValue;
  }

  if (Object.prototype.hasOwnProperty.call(genericMessage, 'stringValue')) {
    return genericMessage.stringValue;
  }

  let protoValue: any = null;
  forEach(genericMessage.protoValue, (proto: any) => {
    if (proto) {
      protoValue = proto;
    }
  });
  if (protoValue === null) {
    logger.error('Could not get a response value from ' + JSON.stringify(genericMessage));
    throw new Error('Could not get a response value from ' + JSON.stringify(genericMessage));
  }

  if (
    Object.getPrototypeOf(protoValue).$type.name === SingleResponseProto.name ||
    Object.getPrototypeOf(protoValue).$type.name === ListResponseProto.name
  ) {
    return unwrap(protoValue.response, Object.getPrototypeOf(protoValue).$type);
  }
  return protoValue;
}

export function isLocalRequest(hostname: string) {
  return (
    includes(hostname, 'localhost:') ||
    includes(hostname, '127.0.0.1:') ||
    hostname.startsWith('192.168.') ||
    includes(hostname, '10.8.') ||
    (hostname.startsWith('100.') &&
      toInteger(hostname.split('.')[1]) >= 64 &&
      toInteger(hostname.split('.')[1]) <= 127)
  );
}

const requestCurrentGeneration: Dictionary<number> = {};
const kRejectedGeneration = 'REJECTED_OLD_GENERATION';
const kRejectedOffline = 'REJECTED_OFFLINE';

function requestProtoToMessageSet(requestProto?: Message, requestScope?: RequestScope) {
  if (!requestProto) {
    return undefined;
  }
  const messageSet = asMessageSet(requestProto.$type, requestProto);
  if (requestScope) {
    messageSet[getExtension(RequestScopeProto)] = RequestScopeProto.fromObject(requestScope);
  }
  return messageSet;
}

function timeoutSignal(timeoutMs: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => {
    controller.abort();
  }, timeoutMs);
  return controller.signal;
}

/**
 * This method can be used to compose multiple AbortController signals so that if any of them are
 * aborted, the resulting signal is aborted.
 */
function anySignal(signals: AbortSignal[]): AbortSignal | undefined {
  if (signals.length === 0) {
    return;
  }
  const controller = new AbortController();

  function onAbort() {
    controller.abort();

    // Cleanup
    for (const signal of signals) {
      signal.removeEventListener('abort', onAbort);
    }
  }

  for (const signal of signals) {
    if (signal.aborted) {
      onAbort();
      break;
    }
    signal.addEventListener('abort', onAbort);
  }

  return controller.signal;
}

export class AjaxActions {
  apiBase: string;
  appConstants: any;
  cacheImpl: any;
  clientHeaders: any;
  logoutAction: () => void;
  defaultFetchTimeoutMs: number;

  constructor({
    apiBase,
    appConstants,
    logoutAction,
    clientHeaders,
    cacheImpl,
    defaultFetchTimeoutMs,
  }: any) {
    this.apiBase = apiBase;
    this.appConstants = appConstants;
    this.cacheImpl = cacheImpl;
    this.clientHeaders = clientHeaders ?? {};
    this.logoutAction = logoutAction;
    this.defaultFetchTimeoutMs = defaultFetchTimeoutMs ?? 0;
  }

  /**
   * Initiates a get request to the Tock API which sends a SingleResponseProto to the store.
   */
  getProto(requestOptions: RequestOptionsTemplate) {
    return this.ajax({
      method: 'get',
      responseProtoType: SingleResponseProto,
      customHeaders: protoHeaders,
      ...requestOptions,
    });
  }

  /**
   * Initiates a get request to the Tock API which sends a ListResponseProto to the store.
   */
  getListProto(requestOptions: RequestOptionsTemplate) {
    return this.ajax({
      method: 'get',
      responseProtoType: ListResponseProto,
      customHeaders: protoHeaders,
      ...requestOptions,
    });
  }

  /**
   * Initiates a get request to the Tock API which sends a file to the store.
   */
  getFile(requestOptions: RequestOptionsTemplate) {
    return this.ajax({
      method: 'get',
      responseProtoType: NonProtoResponseType.File,
      ...requestOptions,
    });
  }

  /**
   * Issues an HTTP GET request and returns the response object directly. This can be useful for uncommon
   * response types and inspecting response headers. Unlike other functions in this file, this doesn't
   * throw an error in the case of non-200 status codes.
   *
   * @returns An object where the 'payload' field is populated with the raw response from the server.
   *
   */
  getRawResponse(requestOptions: RequestOptionsTemplate) {
    return this.ajax({
      method: 'get',
      responseProtoType: NonProtoResponseType.RawResponse,
      ...requestOptions,
    });
  }

  /**
   * Initiates a post request to the Tock API which sends a SingleResponseProto to the store.
   */
  postProto(requestProto: Message, requestOptions: RequestOptionsTemplate) {
    const messageSet = requestProtoToMessageSet(requestProto, requestOptions.requestScope);
    return this.ajax({
      method: 'post',
      requestBody: toArrayBuffer(messageSet),
      requestObj: requestProto,
      responseProtoType: SingleResponseProto,
      customHeaders: protoHeaders,
      ...requestOptions,
    });
  }

  /**
   * Initiates a post request to the Tock API which sends a SingleResponseProto and expects a file response.
   */
  postProtoForFile(requestProto: Message, requestOptions: RequestOptionsTemplate) {
    const messageSet = requestProtoToMessageSet(requestProto, requestOptions.requestScope);
    return this.ajax({
      method: 'post',
      requestBody: toArrayBuffer(messageSet),
      requestObj: requestProto,
      responseProtoType: NonProtoResponseType.File,
      customHeaders: protoHeaders,
      ...requestOptions,
    });
  }

  /**
   * Initiates a post request to the Tock API which sends a ListResponseProto to the store.
   */
  postListProto(requestProto: Message, requestOptions: RequestOptionsTemplate) {
    const messageSet = requestProtoToMessageSet(requestProto);
    return this.ajax({
      method: 'post',
      requestBody: toArrayBuffer(messageSet),
      requestObj: requestProto,
      responseProtoType: ListResponseProto,
      customHeaders: protoHeaders,
      ...requestOptions,
    });
  }

  /**
   * Initiates a post request to the Tock API which sends a file to the store.
   */
  postFile(file: any, requestOptions: RequestOptionsTemplate) {
    return this.ajax({
      method: 'post',
      requestBody: file,
      requestObj: file,
      responseProtoType: SingleResponseProto,
      customHeaders: protoResponseHeaders,
      ...requestOptions,
    });
  }

  /**
   * Initiates a put request to the Tock API which sends a SingleResponseProto to the store.
   */
  putProto(requestProto: Message | undefined, requestOptions: RequestOptionsTemplate) {
    const messageSet = requestProtoToMessageSet(requestProto, requestOptions.requestScope);
    return this.ajax({
      method: 'put',
      requestBody: toArrayBuffer(messageSet),
      requestObj: requestProto || null,
      responseProtoType: SingleResponseProto,
      customHeaders: protoHeaders,
      ...requestOptions,
    });
  }

  /**
   * Initiates a put request to the Tock API which sends a ListResponseProto to the store.
   */
  putListProto(requestProto: Message, requestOptions: RequestOptionsTemplate) {
    const messageSet = requestProtoToMessageSet(requestProto);
    return this.ajax({
      method: 'put',
      requestBody: toArrayBuffer(messageSet),
      requestObj: requestProto,
      responseProtoType: ListResponseProto,
      customHeaders: protoHeaders,
      ...requestOptions,
    });
  }

  /**
   * Initiates a delete request to the Tock API which sends a SingleResponseProto to the store.
   */
  deleteProto(requestOptions: RequestOptionsTemplate) {
    return this.ajax({
      method: 'delete',
      responseProtoType: SingleResponseProto,
      customHeaders: protoHeaders,
      ...requestOptions,
    });
  }

  /**
   * Initiates a delete request to the Tock API which sends a ListResponseProto to the store.
   *
   * @returns {Function} A delayed function to be dispatched to the store by thunk.
   */
  deleteListProto(requestProto: Message, requestOptions: RequestOptionsTemplate) {
    const messageSet = requestProtoToMessageSet(requestProto);
    return this.ajax({
      method: 'delete',
      requestBody: toArrayBuffer(messageSet),
      requestObj: requestProto,
      responseProtoType: ListResponseProto,
      customHeaders: protoHeaders,
      ...requestOptions,
    });
  }

  /**
   * Initiates a batch request with a given set of requests.
   *
   * @returns {Function} A delayed function to be dispatched to the store by thunk.
   */
  batchProto(requests: any[], requestOptions: BatchRequestOptions) {
    const batchRequestListProto = BatchRequestListProto.create();
    batchRequestListProto.request = requests.map((request) => {
      const batchRequest = BatchRequestProto.fromObject({
        method: request.method,
        path: request.path,
      });
      if (request.payload) {
        if (!isObject(request.payload)) {
          throw new Error('Sorry cannot send batch for non proto payloads');
        }
        batchRequest.payload = protoAsMessageSet(request.requestType, request.payload);
      }
      return batchRequest;
    });
    const messageSet = requestProtoToMessageSet(batchRequestListProto);
    return this.ajax({
      path: '/batch/proto',
      method: 'post',
      requestBody: toArrayBuffer(messageSet),
      requestObj: batchRequestListProto,
      responseProtoType: ListResponseProto,
      customHeaders: protoHeaders,
      ...requestOptions,
    });
  }

  dynamicBatch(requests: any[], requestOptions: BatchRequestOptions) {
    const batchRequestListProto = BatchRequestListProto.create();
    const actions = map(requests, (request) => request.action);
    batchRequestListProto.request = requests.map((request: any) => {
      const batchRequest = BatchRequestProto.fromObject({
        method: request.method,
        path: request.path,
      });
      if (request.payload) {
        if (!isObject(request.payload)) {
          throw new Error('Sorry cannot send batch for non proto payloads');
        }
        batchRequest.payload = protoAsMessageSet(request.payload.$type, request.payload);
      }
      return batchRequest;
    });
    const requestScope: RequestScope = requests.find(
      (request) => request.requestScope !== undefined
    )?.requestScope;
    const messageSet = requestProtoToMessageSet(batchRequestListProto, requestScope);

    const sendStartingActions = (dispatch: Dispatch) => {
      forEach(actions, (action: any) => {
        const actionType = action.type;
        dispatch({
          type: this.appConstants[actionType + '_STARTING'],
          context: action.context,
          request: action.request,
        });
      });
    };

    const sendDoneActions = (dispatch: Dispatch, responses: any) => {
      batch(() => {
        forEach(responses, (response: any, idx: any) => {
          const action = actions[idx];
          const actionType = action.type;
          const actionSuccessMessage = action.toast;
          dispatch({
            type: this.appConstants[actionType + '_DONE'],
            payload: response,
            request: action.request,
            context: action.context,
            toastMessage: actionSuccessMessage
              ? {
                  type: 'info',
                  message: actionSuccessMessage,
                }
              : null,
          } as any);
        });
      });
    };

    const sendErrorActions = (dispatch: Dispatch, error: libProto.lib.ErrorMessageProto) => {
      batch(() => {
        let errorMessage = error.message;
        const errorCode = safeToNumber(error.errorCode);
        const errorMessages = [errorMessage];
        forEach(actions, (action: any) => {
          const actionType = action.type.toString();
          const actionErrorMapper = action.onError;

          if (actionErrorMapper) {
            errorMessage = getMappedErrorMessage(
              dispatch,
              actionErrorMapper,
              errorCode,
              errorMessage
            );
            errorMessages.push(errorMessage);
          }

          this.sendError({
            typePrefix: actionType,
            message: errorMessage,
            error,
            requestObj: action.request,
            context: action.context,
            options: {
              dontThrow: true,
            },
            dispatch,
          });
        });
        const requestedPaths = map(batchRequestListProto.request, (r: { path: string }) => {
          return r.path;
        });
        logger.error(
          'Batch request to the following paths failed (truncated to 20):',
          take(requestedPaths, 20)
        );
        throw new BatchRequestError(`Batch request failed: ${errorMessages.join(',')}`, error);
      });
    };

    return this.ajax(
      {
        path: '/batch/proto',
        method: 'post',
        requestBody: toArrayBuffer(messageSet),
        requestObj: batchRequestListProto,
        responseProtoType: ListResponseProto,
        customHeaders: protoHeaders,
        ...requestOptions,
      },
      {
        sendDoneActions,
        sendErrorActions,
        sendStartingActions,
      }
    );
  }

  /**
   * Common method shared for all ajax calls. This method dispatches starting, failed and done events during
   * the life-cycle of the ajax request and notifies the store.
   *
   * @returns {Function} A delayed function to be dispatched to the store by thunk.
   */
  ajax(requestOptions: RequestOptionsObject, dynamicBatchOptions: any = {}) {
    // this is used if ajax actions is being used outside of redux
    // batch actions are not supported
    if (requestOptions.ignoreDispatch) {
      return this.callApi(requestOptions, dynamicBatchOptions);
    } else {
      return (dispatch: Dispatch) => {
        return this.callApi(requestOptions, dynamicBatchOptions, dispatch);
      };
    }
  }

  /**
   * dispatch is sent by default if used in the context of tock api middleware, its not there typically because
   * the api client is being used outside of redux
   * @param requestOptions
   * @param dynamicBatchOptions
   * @param dispatch
   */
  callApi(requestOptions: RequestOptionsObject, dynamicBatchOptions: any, dispatch?: Dispatch) {
    const {
      app,
      experimentVariantHeader,
      cacheOptions,
      context,
      customHeaders,
      discardOldRequests,
      errorMapper,
      method,
      onSuccess,
      path,
      requestBody,
      requestObj,
      responseProtoType,
      scope,
      shippingState,
      signal,
      successMessage,
      syndicate,
      type,
      ignoreNetworkErrors,
      timeoutMs,
      metroAreaId,
    } = requestOptions;

    const requestAbsPath = `${this.apiBase}${path}`;
    const typePrefix = type.toString();
    const startTime = new Date().getTime();

    if (dynamicBatchOptions.sendStartingActions && dispatch) {
      dynamicBatchOptions.sendStartingActions(dispatch);
    }
    if (dispatch) {
      dispatch({
        type: this.appConstants[typePrefix + '_STARTING'],
        request: requestObj,
        context,
      });
    }

    logger.networking(method, path, 'STARTING', ...filter([requestObj], (e: any) => !!e));
    const businessHeaders = getBusinessHeader(app, scope, syndicate);
    const experimentsHeader = getExperimentsHeader(experimentVariantHeader);
    const shippingStateHeader = getShippingStateHeader(shippingState);
    const metroAreaHeader = getMetroAreaHeader(metroAreaId);
    const headers = {
      ...this.clientHeaders,
      ...(customHeaders || {}),
      ...businessHeaders,
      ...experimentsHeader,
      ...shippingStateHeader,
      ...metroAreaHeader,
    };

    let cacheKey!: string;

    Object.keys(headers).forEach((key: any) => {
      if (!headers[key]) {
        delete headers[key];
      }
    });
    const useSameOrigin = !(BROWSER && isLocalRequest(window.location.host));

    // Set up composite abort signal, if necessary.
    const signals: AbortSignal[] = [];
    if (signal) {
      signals.push(signal);
    }
    if (timeoutMs !== undefined && timeoutMs > 0) {
      signals.push(timeoutSignal(timeoutMs));
    } else if (this.defaultFetchTimeoutMs > 0) {
      signals.push(timeoutSignal(this.defaultFetchTimeoutMs));
    }

    const request: any = {
      credentials: useSameOrigin ? 'same-origin' : 'include',
      method,
      headers,
      signal: anySignal(signals),
    };

    if (cacheOptions && cacheOptions.cacheControl) {
      request.cache = cacheOptions.cacheControl;
    }

    if (requestBody) {
      request.body = requestBody;
    }

    let currentGeneration = 0;
    const generationKey = typePrefix + method + requestAbsPath;
    if (discardOldRequests) {
      currentGeneration = requestCurrentGeneration[generationKey] || 0;
      currentGeneration++;
      requestCurrentGeneration[generationKey] = currentGeneration;
    }

    let cachedValue: any = null;
    if (cacheOptions && cacheOptions.enableCache) {
      if (!this.cacheImpl) {
        logger.error('Request has cache enabled but the middleware has no cache implementation!');
      } else {
        cacheKey = JSON.stringify({
          method,
          path: `${this.apiBase}${path}`,
          businessHeaders,
          request: requestObj ? toJSON(requestObj) : null,
        });

        const value = this.cacheImpl.get(cacheKey);
        if (value && Date.now() - value.timestamp < (cacheOptions.maxAge || 60000)) {
          cachedValue = value.cachedValue;
        } else if (value) {
          this.cacheImpl.delete(cacheKey);
        }
      }
    }
    let promise: any = null;
    if (cachedValue) {
      logger.debug(`Cache hit for cache key ${cacheKey}`);

      // Error response from the API.
      if (cachedValue.error) {
        promise = Promise.reject(cachedValue);
      } else {
        let response;
        switch (responseProtoType) {
          case NonProtoResponseType.File:
            response = cachedValue.response;
            break;
          default:
            response = unwrap(cachedValue.response, responseProtoType);
        }
        promise = Promise.resolve({
          type: this.appConstants[typePrefix + '_DONE'],
          payload: response,
          request: requestObj,
          context,
          toastMessage: successMessage
            ? {
                type: 'info',
                message: successMessage,
              }
            : null,
        }).then((response) => {
          if (dispatch) {
            dispatch(response as any);
          }
          return response;
        });
      }
    } else {
      promise = fetch(requestAbsPath, request).then((httpResponse: any) => {
        const isNoContentResponse = httpResponse.status === 204;
        const isRawResponse = responseProtoType === NonProtoResponseType.RawResponse;
        if (httpResponse.status !== 200 && !isNoContentResponse && !isRawResponse) {
          throw httpResponse;
        }

        return readStream(httpResponse).then((buffer: any) => {
          const responseProto = getResponseProto(responseProtoType, httpResponse, buffer);
          if (cacheOptions?.enableCache) {
            if (!this.cacheImpl) {
              logger.error(
                'Request has cache enabled but the middleware has no cache implementation!'
              );
            } else if (
              !responseProto.error ||
              (safeToNumber(responseProto.error.errorCode) !== 1000 &&
                safeToNumber(responseProto.error.errorCode) !== 1007)
            ) {
              this.cacheImpl.set(cacheKey, {
                cachedValue: responseProto,
                timestamp: Date.now(),
              });
            }
          }

          // Error response from the API.
          if (responseProto.error) {
            throw responseProto;
          }

          const response = isNoContentResponse ? {} : getResponse(responseProtoType, responseProto);

          logger.networking(
            method,
            path,
            'DONE',
            response,
            `took ${new Date().getTime() - startTime}ms`
          );
          logSucessMockInfoIfNeeded(requestOptions, response);
          if (dynamicBatchOptions.sendDoneActions && dispatch) {
            dynamicBatchOptions.sendDoneActions(dispatch, response);
          }
          if (discardOldRequests && requestCurrentGeneration[generationKey] !== currentGeneration) {
            throw new Error(kRejectedGeneration);
          }
          if (dispatch) {
            return dispatch({
              type: this.appConstants[typePrefix + '_DONE'],
              payload: response,
              request: requestObj,
              context,
              toastMessage: successMessage
                ? {
                    type: 'info',
                    message: successMessage,
                  }
                : null,
            } as any);
          }
          return response;
        });
      });
    }

    return promise
      .then((response: any) => {
        if (onSuccess) {
          onSuccess(response);
        }
        return response;
      })
      .catch((error: { error: libProto.lib.ErrorMessageProto; message?: string }) => {
        if (error && error.message === kRejectedGeneration) {
          logger.networking('Rejected old request', method, path);
          return;
        }
        // Aborted requests
        if (error && (error as any).code === 20 && (error as any).name === 'AbortError') {
          logger.networking('Aborted request', method, path);
          return;
        }

        logger.networking(method, path, 'ERROR', error);
        // Check if the error response is a proto.
        if (isObject(error) && error.error && error.error.errorCode) {
          const responseProto = error;
          let errorMessage = responseProto.error.message;
          const errorCode = safeToNumber(responseProto.error.errorCode);

          if (errorCode === 5032) {
            if (BROWSER) {
              window.location.reload();
              return;
            } else {
              // This prevents all the normal error handling from happening and
              // actually causes consumer to initialize on the client rather
              // than with server side rendering. We could instead catch this
              // error in the error handler and redirect through a path on
              // apiserver to set the cookie to solve this correctly, However,
              // that automatically happens as the page initializes in the BROWSER
              // path above. This should rarely happen outside of the transition
              // period to the new cookie setting method on apiserver, so I'm
              // just leaving it as-is.
              throw error;
            }
          }

          if (errorMapper && dispatch) {
            errorMessage = getMappedErrorMessage(dispatch, errorMapper, errorCode, errorMessage);
          }

          // If we get an access denied message, log the user out.
          if (errorCode === 1005) {
            this.logoutAction();
          }

          if (dynamicBatchOptions.sendErrorActions) {
            return dynamicBatchOptions.sendErrorActions(dispatch, responseProto.error);
          }

          // sendError ends in a throw, stopping the rest of the code from executing, acting like a return
          this.sendError({
            typePrefix,
            message: errorMessage,
            error,
            requestObj,
            context,
            dispatch,
          });
        }

        // This means you got an HTTP error. Log the error.
        logger.error(method, path, 'ERROR', error, `took ${new Date().getTime() - startTime}ms`);

        let errorMessage = DEFAULT_AJAX_ERROR_MESSAGE;

        // If you encounter an HTTP access denied message, log the user out.
        if (error && (error as any).status === 403) {
          errorMessage =
            'You don’t have access to that page. If you believe that’s incorrect, please try again.';
        }

        // 405 is an error code we are using from haproxy to force reload.
        if (error && (error as any).status === 405) {
          errorMessage =
            'You’re using an old version of Tock. We are refreshing your browser to make sure you have the latest updates.';
          if (BROWSER) {
            setTimeout(() => {
              // this used to have `reload(true)` but check this out: https://developer.mozilla.org/en-US/docs/Web/API/Location/reload#location.reload_has_no_parameter
              window.location.reload();
            }, 300);
          }
        }

        if (ignoreNetworkErrors) {
          return;
        }

        this.sendError({
          typePrefix,
          message: errorMessage,
          error,
          requestObj,
          context,
          dispatch,
        });
      });
  }

  sendError({
    typePrefix,
    message,
    error,
    requestObj,
    context,
    options = {},
    dispatch,
  }: {
    typePrefix: string;
    message: string;
    error: any;
    requestObj: any;
    context: any;
    options?: any;
    dispatch?: Dispatch;
  }) {
    if (
      BROWSER &&
      (window as any).Rollbar &&
      isError(error) &&
      error.message !== kRejectedGeneration &&
      error.message !== kRejectedOffline
    ) {
      (window as any).Rollbar.error(message, error);
    }

    let toastMessage: any = {
      type: 'error',
      message,
    };

    if (error === kRejectedOffline) {
      toastMessage.message =
        'Your device is offline, check your internet connection and try again.';
    }

    if (message && message.indexOf('TypeError: cancelled') >= 0) {
      toastMessage = undefined;
    }

    if (dispatch) {
      dispatch({
        type: this.appConstants[typePrefix + '_FAILED'],
        request: requestObj,
        context,
        toastMessage,
        error: error.error,
      });
    }

    if (options.dontThrow) {
      return;
    }
    throw error;
  }
}

function logSucessMockInfoIfNeeded(requestOptions: RequestOptionsObject, response: any) {
  if (!BROWSER || !(window as any).LOG_NETWORK_MOCKS) {
    return;
  }

  let logNetworkMocks = false;
  try {
    logNetworkMocks = !!localStorage.getItem('logNetworkMocks');
  } catch (e) {
    if (e instanceof Error) {
      logger.warn(e.toString());
    }
    return;
  }
  if (logNetworkMocks) {
    let payload;
    if (Array.isArray(response)) {
      payload = toJSONArray(response);
    } else {
      payload = toJSON(response);
    }

    const stringifiedRequestResponse = JSON.stringify(
      {
        request: {
          path: requestOptions.path,
          method: requestOptions.method,
        },
        response: {
          payload,
        },
      },
      null,
      2
    );
    const requestResponseWithProtoTypeInserted = replace(
      stringifiedRequestResponse,
      '"payload": {',
      `"payload": new ${split(response.$type, '.tocktix.messages.')[1]}({`
    );
    logger.info(requestResponseWithProtoTypeInserted);
  }
}
